/**
 * Phase 23: Plan tiers, limits, and pricing.
 */

import type { Subscription } from '@/lib/billing/api';

export type Tier = 'starter' | 'pro' | 'enterprise';

export interface TierLimits {
  /** Paid editor seats (create/edit access). Billed per editor. */
  maxEditors: number;
  /** Free read-only viewer seats (sponsors, execs, stakeholders). */
  maxViewers: number;
  maxPortfolios: number;
  maxDocumentStorageGb: number;
  realtime: boolean;
  customCurrency: boolean;
}

export type BillingCycle = 'monthly' | 'annual';

export interface TierDefinition {
  key: Tier;
  name: string;
  tagline: string;
  /** Per-editor price billed monthly. Null = custom (Enterprise). */
  pricePerSeatMonthly: number | null;
  /** Per-editor effective monthly rate when billed annually (~2 months free). */
  pricePerSeatAnnual: number | null;
  stripePriceIdMonthly: string | null;
  stripePriceIdAnnual: string | null;
  limits: TierLimits;
  features: string[];
}

// Vite only statically replaces STATIC import.meta.env.X access — never use a
// dynamic key here. Monthly falls back to the legacy pre-annual env var.
const STRIPE_PRICE_STARTER_MONTHLY =
  (import.meta.env.VITE_STRIPE_PRICE_STARTER_MONTHLY as string) ||
  (import.meta.env.VITE_STRIPE_PRICE_STARTER as string) || '';
const STRIPE_PRICE_STARTER_ANNUAL = (import.meta.env.VITE_STRIPE_PRICE_STARTER_ANNUAL as string) || '';
const STRIPE_PRICE_PRO_MONTHLY =
  (import.meta.env.VITE_STRIPE_PRICE_PRO_MONTHLY as string) ||
  (import.meta.env.VITE_STRIPE_PRICE_PRO as string) || '';
const STRIPE_PRICE_PRO_ANNUAL = (import.meta.env.VITE_STRIPE_PRICE_PRO_ANNUAL as string) || '';

export const TIERS: Record<Tier, TierDefinition> = {
  starter: {
    key: 'starter',
    name: 'Starter',
    tagline: 'For solo PMs and small teams.',
    pricePerSeatMonthly: 5,
    pricePerSeatAnnual: 4,
    stripePriceIdMonthly: STRIPE_PRICE_STARTER_MONTHLY || null,
    stripePriceIdAnnual: STRIPE_PRICE_STARTER_ANNUAL || null,
    limits: {
      maxEditors: 5,
      maxViewers: 10,
      maxPortfolios: 1,
      maxDocumentStorageGb: 1,
      realtime: false,
      customCurrency: false,
    },
    features: [
      'Up to 5 editors',
      '10 free viewers',
      'Unlimited projects',
      '1 portfolio',
      'Schedule, risks, issues, changes & baselines',
      '1 GB document storage',
      'Email support',
    ],
  },
  pro: {
    key: 'pro',
    name: 'Pro',
    tagline: 'For teams running multiple programs.',
    pricePerSeatMonthly: 12,
    pricePerSeatAnnual: 10,
    stripePriceIdMonthly: STRIPE_PRICE_PRO_MONTHLY || null,
    stripePriceIdAnnual: STRIPE_PRICE_PRO_ANNUAL || null,
    limits: {
      maxEditors: 25,
      maxViewers: Infinity,
      maxPortfolios: 10,
      maxDocumentStorageGb: 25,
      realtime: true,
      customCurrency: true,
    },
    features: [
      'Up to 25 editors',
      'Unlimited free viewers',
      'Unlimited projects',
      'Up to 10 portfolios',
      'Programs & portfolio roll-ups',
      'AI insights & executive briefings',
      'Intake & demand + lessons-learned',
      'Real-time collaboration',
      '25 GB document storage',
      'Priority support',
    ],
  },
  enterprise: {
    key: 'enterprise',
    name: 'Enterprise',
    tagline: 'For larger organizations with custom needs.',
    pricePerSeatMonthly: null,
    pricePerSeatAnnual: null,
    stripePriceIdMonthly: null,
    stripePriceIdAnnual: null,
    limits: {
      maxEditors: Infinity,
      maxViewers: Infinity,
      maxPortfolios: Infinity,
      maxDocumentStorageGb: Infinity,
      realtime: true,
      customCurrency: true,
    },
    features: [
      'Everything in Pro',
      'Unlimited editors, viewers & portfolios',
      'Unlimited projects & storage',
      'SSO / SAML (coming soon)',
      'Advanced security & admin controls',
      'Dedicated support & onboarding',
      'SLA guarantee',
    ],
  },
};

export const ORDERED_TIERS: TierDefinition[] = [TIERS.starter, TIERS.pro, TIERS.enterprise];

const ACTIVE_STATUSES: ReadonlySet<Subscription['status']> = new Set<Subscription['status']>([
  'trialing', 'active', 'past_due',
]);

export function hasActiveAccess(sub: Subscription | null): boolean {
  if (!sub) return false;
  return ACTIVE_STATUSES.has(sub.status);
}

export function isTrialing(sub: Subscription | null): boolean {
  return sub?.status === 'trialing';
}

export function trialDaysRemaining(sub: Subscription | null): number {
  if (!sub || !isTrialing(sub) || !sub.trial_end) return 0;
  const end = new Date(sub.trial_end).getTime();
  const now = Date.now();
  if (end <= now) return 0;
  return Math.ceil((end - now) / (24 * 60 * 60 * 1000));
}

export function isGrandfathered(sub: Subscription | null): boolean {
  return sub?.stripe_customer_id === 'GRANDFATHERED';
}

/**
 * Phase 24: distinguishes the two kinds of no-card trial.
 *
 * Both grandfathered workspaces (migration 0011) and brand-new signups
 * (migration 0013) get a trial row with stripe_customer_id = 'GRANDFATHERED'
 * — that placeholder means "no real Stripe customer yet" and lots of code
 * (checkout, portal, sync-seats) depends on it.
 *
 * But they differ in length: grandfathered trials are 30 days, auto-trials
 * for new signups are 14. We tell them apart by measuring the trial window
 * rather than overloading the placeholder string. This is display-only — it
 * decides what wording the Billing page and TrialBadge show.
 *
 *   'grandfathered' — ~30-day trial granted to a pre-billing workspace
 *   'signup'        — ~14-day trial granted automatically on signup
 *   'paid'          — trial attached to a real Stripe subscription (has a card)
 *   'none'          — not on a trial
 */
export function trialKind(
  sub: Subscription | null,
): 'grandfathered' | 'signup' | 'paid' | 'none' {
  if (!sub || !isTrialing(sub) || !sub.trial_start || !sub.trial_end) return 'none';
  // A real Stripe customer means the trial came from Checkout (card on file).
  if (sub.stripe_customer_id !== 'GRANDFATHERED') return 'paid';
  // No-card trial: classify by length. 14-day signup vs 30-day grandfathered.
  // Use a 21-day midpoint as the threshold to absorb clock/rounding skew.
  const start = new Date(sub.trial_start).getTime();
  const end = new Date(sub.trial_end).getTime();
  const days = (end - start) / (24 * 60 * 60 * 1000);
  return days > 21 ? 'grandfathered' : 'signup';
}

export function tierFor(sub: Subscription | null): TierDefinition {
  if (!sub) return TIERS.starter;
  return TIERS[sub.tier as Tier] ?? TIERS.starter;
}

/**
 * Phase 30.19 (Budget B5+ AI gate): does this workspace have AI Insights?
 *
 * AI is a Pro-tier (and above) feature. Requires:
 *   - An active subscription (trialing/active/past_due — same definition as
 *     hasActiveAccess; we don't degrade AI on the first failed payment)
 *   - Tier of 'pro' or 'enterprise'
 *
 * Returns false when sub is null (no workspace billing data yet — e.g.,
 * brand-new signup before the auto-trial migration has run, or a freshly
 * grandfathered workspace pre-Phase-11).
 */
export function canUseAI(sub: Subscription | null): boolean {
  if (!hasActiveAccess(sub)) return false;
  return sub?.tier === 'pro' || sub?.tier === 'enterprise';
}

/**
 * Benefits Realization is a paid module available on Pro and Enterprise
 * (gated identically to AI Insights). Returns false without active access.
 */
export function canUseBenefits(sub: Subscription | null): boolean {
  if (!hasActiveAccess(sub)) return false;
  return sub?.tier === 'pro' || sub?.tier === 'enterprise';
}

/**
 * Intake & Demand Management is a paid module on Pro and Enterprise.
 */
export function canUseDemands(sub: Subscription | null): boolean {
  if (!hasActiveAccess(sub)) return false;
  return sub?.tier === 'pro' || sub?.tier === 'enterprise';
}

/** Lessons-Learned + AI retrieval is a paid module on Pro and Enterprise. */
export function canUseLessons(sub: Subscription | null): boolean {
  if (!hasActiveAccess(sub)) return false;
  return sub?.tier === 'pro' || sub?.tier === 'enterprise';
}

/** Executive Briefing generator is a paid module on Pro and Enterprise. */
export function canUseBriefings(sub: Subscription | null): boolean {
  if (!hasActiveAccess(sub)) return false;
  return sub?.tier === 'pro' || sub?.tier === 'enterprise';
}

/** True only on the Enterprise tier (with active access). */
export function isEnterprise(sub: Subscription | null): boolean {
  if (!hasActiveAccess(sub)) return false;
  return sub?.tier === 'enterprise';
}

/**
 * Gates the enterprise resource-management model (rate cards, multi-role +
 * skill proficiency, managed departments). Enterprise-tier only. When this is
 * false the app forces Standard resource mode regardless of the stored value,
 * so a downgrade hides the enterprise UI without losing data.
 * See docs/resources-enterprise-model-spec.md.
 */
export function canUseEnterpriseResources(sub: Subscription | null): boolean {
  return isEnterprise(sub);
}

export interface LimitCheckResult {
  allowed: boolean;
  current: number;
  limit: number;
  unlimited: boolean;
  upgradeTo: Tier | null;
}

export function checkLimit(
  sub: Subscription | null,
  metric: keyof TierLimits,
  nextCount: number,
): LimitCheckResult {
  const tier = tierFor(sub);
  const limit = tier.limits[metric];
  if (typeof limit === 'boolean') {
    return {
      allowed: limit,
      current: 0,
      limit: 0,
      unlimited: false,
      upgradeTo: limit ? null : findUpgrade(tier.key, metric),
    };
  }
  const unlimited = !isFinite(limit);
  const allowed = unlimited || nextCount <= limit;
  return {
    allowed,
    current: nextCount,
    limit: unlimited ? Infinity : limit,
    unlimited,
    upgradeTo: allowed ? null : findUpgrade(tier.key, metric),
  };
}

function findUpgrade(currentTier: Tier, metric: keyof TierLimits): Tier | null {
  const currentIdx = ORDERED_TIERS.findIndex((t) => t.key === currentTier);
  for (let i = currentIdx + 1; i < ORDERED_TIERS.length; i++) {
    const t = ORDERED_TIERS[i];
    const limit = t.limits[metric];
    if (typeof limit === 'boolean') {
      if (limit) return t.key;
    } else {
      if (!isFinite(limit) || limit > 0) return t.key;
    }
  }
  return null;
}

/** Per-editor price for the chosen billing cycle (annual = effective monthly). */
export function pricePerSeatFor(tier: TierDefinition, cycle: BillingCycle): number | null {
  return cycle === 'annual' ? tier.pricePerSeatAnnual : tier.pricePerSeatMonthly;
}

/** Stripe price id for the chosen billing cycle. */
export function priceIdForCycle(tier: TierDefinition, cycle: BillingCycle): string | null {
  return cycle === 'annual' ? tier.stripePriceIdAnnual : tier.stripePriceIdMonthly;
}

export function priceLabel(tier: TierDefinition, cycle: BillingCycle = 'monthly'): string {
  const price = pricePerSeatFor(tier, cycle);
  if (price === null) return 'Custom';
  return `$${price}/editor/month`;
}

export function monthlyTotal(tier: TierDefinition, seatCount: number): number | null {
  if (tier.pricePerSeatMonthly === null) return null;
  return tier.pricePerSeatMonthly * seatCount;
}
